import {zodResolver} from "@hookform/resolvers/zod";
import {Loader2} from "lucide-react";
import {useForm} from "react-hook-form";

import {Button} from "@/components/ui/button";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {Input} from "@/components/ui/input";
import {cn} from "@/lib/utils";
import {PasswordInput} from "@/src/modules/auth/components";

import {claimAccountSchema, type ClaimAccountInput} from "../schemas/claim-account";
import type {ResidentClaimStartData} from "../types";
import ClaimContextCard from "./claim-context-card";

type ClaimAccountStepProps = {
  claimCode: string;
  startData: ResidentClaimStartData;
  onSubmit: (values: ClaimAccountInput) => void;
  isPending: boolean;
  errorMessage?: string;
  fieldErrors?: Record<string, string[]>;
  onBackToCode?: () => void;
};

export default function ClaimAccountStep({
  startData,
  onSubmit,
  isPending,
  errorMessage,
  fieldErrors,
  onBackToCode,
}: ClaimAccountStepProps) {
  const form = useForm<ClaimAccountInput>({
    defaultValues: {
      email: "",
      password: "",
      password_confirmation: "",
    },
    resolver: zodResolver(claimAccountSchema),
  });

  const getServerFieldError = (field: keyof ClaimAccountInput) => fieldErrors?.[field]?.[0];

  return (
    <div className="flex flex-col gap-4">
      <ClaimContextCard property={startData.property} household={startData.household} />

      <Form {...form}>
        <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="email"
            render={({field, fieldState}) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder="you@example.com"
                    disabled={isPending}
                    autoComplete="email"
                  />
                </FormControl>
                {fieldState.error ? (
                  <FormError error={fieldState.error.message} />
                ) : getServerFieldError("email") ? (
                  <FormError error={getServerFieldError("email")} />
                ) : null}
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({field, fieldState}) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput {...field} disabled={isPending} autoComplete="new-password" />
                </FormControl>
                {fieldState.error ? (
                  <FormError error={fieldState.error.message} />
                ) : getServerFieldError("password") ? (
                  <FormError error={getServerFieldError("password")} />
                ) : null}
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password_confirmation"
            render={({field, fieldState}) => (
              <FormItem>
                <FormLabel>Confirm password</FormLabel>
                <FormControl>
                  <PasswordInput {...field} disabled={isPending} autoComplete="new-password" />
                </FormControl>
                {fieldState.error ? (
                  <FormError error={fieldState.error.message} />
                ) : getServerFieldError("password_confirmation") ? (
                  <FormError error={getServerFieldError("password_confirmation")} />
                ) : null}
              </FormItem>
            )}
          />

          {errorMessage ? <FormError error={errorMessage} /> : null}

          <Button
            type="submit"
            className={cn("w-full", isPending && "cursor-not-allowed opacity-80")}
            disabled={isPending}
          >
            {isPending ? (
              <>
                Submitting...
                <Loader2 className="ml-2 size-4 animate-spin" />
              </>
            ) : (
              "Create account"
            )}
          </Button>

          {onBackToCode ? (
            <Button type="button" variant="ghost" className="w-full" onClick={onBackToCode}>
              Enter a different code
            </Button>
          ) : null}
        </form>
      </Form>
    </div>
  );
}
